/**
 * MyCrewsSection (P9 pilot)
 * ─────────────────────────
 * Profile-tab-only, read-only display of contractor-owned crews and their
 * derived metrics. No writes, no scoring, no ranking.
 *
 * Hidden entirely behind FEATURE_FLAGS.crewInsightsEnabled — when off, the
 * component returns null AND no crew selectors run.
 */

import { useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Users } from "lucide-react";
import { useAppData } from "@/data/AppDataContext";
import { useCurrentContractor } from "@/hooks/useCurrentContractor";
import { isFeatureEnabled } from "@/data/featureFlags";
import {
  getCrewsForContractor,
  getCrewMetrics,
  type CrewMetrics,
} from "@/data/contractor/crew";
import { getJobCompletionDate } from "@/data/contractor/commandCenter";

function pct(rate: number | null): string {
  return rate == null ? "—" : `${Math.round(rate * 100)}%`;
}

function getJobQaCounts(job: Parameters<typeof getJobCompletionDate>[0]) {
  // Crew metrics are derived from jobs only — and our Job model doesn't carry
  // QA counts directly. For the pilot, treat "completed without issueFlag" as
  // a passed proxy and "completed with issueFlag" as a failed proxy. This is
  // intentionally conservative: the same data is available company-wide so
  // the comparison stays apples-to-apples.
  const completion = getJobCompletionDate(job);
  if (!completion) return { passed: 0, failed: 0 };
  return job.issueFlag ? { passed: 0, failed: 1 } : { passed: 1, failed: 0 };
}

function MetricCell({
  label,
  value,
  insufficient,
}: {
  label: string;
  value: string;
  insufficient: boolean;
}) {
  return (
    <div>
      <p className="text-[9px] text-muted-foreground uppercase tracking-wider">{label}</p>
      {insufficient ? (
        <Badge variant="outline" className="mt-0.5 text-[9px] bg-muted text-muted-foreground border-border">
          Not enough data
        </Badge>
      ) : (
        <p className="text-sm font-semibold mt-0.5 tabular-nums">{value}</p>
      )}
    </div>
  );
}

function CrewRow({ crew, metrics }: {
  crew: ReturnType<typeof getCrewsForContractor>[number];
  metrics: CrewMetrics;
}) {
  return (
    <div
      data-testid="crew-row"
      data-crew-id={crew.id}
      className="rounded-lg border border-border bg-card p-3"
    >
      <div className="flex items-start justify-between gap-2 mb-2.5">
        <div className="min-w-0">
          <p className="text-sm font-semibold truncate">{crew.name}</p>
          <p className="text-[11px] text-muted-foreground truncate">
            Lead: {crew.leadInstallerName}
          </p>
        </div>
        <Badge
          variant="outline"
          className={`text-[9px] ${
            crew.active
              ? "bg-success/10 text-success border-success/20"
              : "bg-muted text-muted-foreground border-border"
          }`}
        >
          {crew.active ? "Active" : "Inactive"}
        </Badge>
      </div>
      <div className="grid grid-cols-3 gap-2 pt-2.5 border-t border-border/50">
        <MetricCell
          label="Completed"
          value={String(metrics.completedCount)}
          insufficient={false}
        />
        <MetricCell
          label="QA pass"
          value={pct(metrics.qaPassRate)}
          insufficient={metrics.qaPassRate == null}
        />
        <MetricCell
          label="On-time"
          value={pct(metrics.onTimeCompletionRate)}
          insufficient={metrics.onTimeCompletionRate == null}
        />
      </div>
    </div>
  );
}

export function MyCrewsSection() {
  // HARD GUARD: bail before any selectors run.
  if (!isFeatureEnabled("crewInsightsEnabled")) return null;

  const { crews, jobs } = useAppData();
  const { id: contractorId } = useCurrentContractor();

  const myCrews = useMemo(
    () => getCrewsForContractor(crews, contractorId),
    [crews, contractorId],
  );

  const rows = useMemo(
    () =>
      myCrews.map((crew) => ({
        crew,
        metrics: getCrewMetrics({
          crewId: crew.id,
          jobs,
          getCompletionDate: getJobCompletionDate,
          getJobQaCounts,
        }),
      })),
    [myCrews, jobs],
  );

  if (myCrews.length === 0) return null;

  return (
    <Card data-testid="my-crews-section">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-1.5">
          <Users className="h-3.5 w-3.5 text-muted-foreground" />
          <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
            My Crews
          </h3>
        </div>
        <div className="space-y-2">
          {rows.map(({ crew, metrics }) => (
            <CrewRow key={crew.id} crew={crew} metrics={metrics} />
          ))}
        </div>
        <p className="text-[10px] text-muted-foreground">
          Informational only. Crew metrics don’t affect dispatch or your tier.
        </p>
      </CardContent>
    </Card>
  );
}

export default MyCrewsSection;
